import * as http from 'http'
import * as https from 'https'
// import _ from 'lodash'
import axios from 'axios'
import localforage from 'localforage'
import memoryDriver from 'localforage-memoryStorageDriver'
import { getApiBaseUrl } from '~/utils/apiUrl'

class AxiosBuilderPostCache {
  config = {
    baseURL: getApiBaseUrl(),
    headers: {},
    params: {},
    withCredentials: true,
    httpAgent: new http.Agent({
      keepAlive: true,
      keepAliveMsecs: 300000,
      scheduling: 'lifo',
      maxSockets: 128,
      maxFreeSockets: 128,
      timeout: 60000,
      freeSocketTimeout: 30000
    }),
    httpsAgent: new https.Agent({
      keepAlive: true,
      keepAliveMsecs: 300000,
      scheduling: 'lifo',
      maxSockets: 128,
      maxFreeSockets: 128,
      timeout: 60000,
      freeSocketTimeout: 30000,
      rejectUnauthorized: false
    })
  }

  requestInterceptors = []
  responseInterceptors = []

  constructor () {
    this.cancelTokenSources = [] // 진행 중인 요청의 취소 토큰을 저장

    if (typeof window !== 'undefined' && window.indexedDB) {
      // IndexedDB로 캐시 저장을 위한 설정
      localforage.defineDriver(memoryDriver)

      // 'postCache'가 'daiso-cache'라는 name 아래에 존재하는지 확인
      this.forageStore = localforage.createInstance({
        driver: [localforage.INDEXEDDB],
        name: 'daiso-cache', // 데이터베이스 이름
        storeName: 'postCache' // 실제 저장소 이름
      })

      // 데이터베이스 초기화가 완료될 때까지 대기
      this.forageStore.ready().then(() => {
        console.log('localforage initialized successfully with IndexedDB')
      }).catch((error) => {
        console.error('Error initializing localforage with IndexedDB:', error)
      })
    }
  }

  // 캐시 키 생성 함수 (GET과 POST를 모두 지원)
  _getCacheKey (config) {
    let cacheKey = config.url
    if (config.method === 'post' && config.data) {
      // POST 요청의 body 데이터를 쿼리 문자열로 변환
      const params = JSON.stringify(config.data)
      cacheKey += '?' + params
    } else if (config.method === 'get' && config.params) {
      // GET 요청의 파라미터를 쿼리 문자열로 변환
      const params = this.convertObjectToQueryString(config.params)
      cacheKey += `?${params}`
    }

    return cacheKey.replace(/\\"/g, "'").replace(/\\/g, "'").replace(/"/g, "'").replace(/'{/g, '{').replace(/}'/g, '}')
  }

  // 객체를 쿼리 문자열로 변환하는 함수
  convertObjectToQueryString (obj) {
    let queryString = ''
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // 객체의 키와 값을 쿼리 문자열 형식으로 변환
        if (queryString.length > 0) {
          queryString += '&'
        }
        queryString += `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
      }
    }
    return queryString
  }

  // 캐시된 데이터가 있으면 반환
  async _getCache (time, config) {
    try {
      // localforage가 준비되었는지 확인
      const isReady = await this.forageStore.ready()
        .then(() => true)
        .catch(e => false)

      if (!isReady) {
        return null
      }

      const cacheKey = this._getCacheKey(config)

      const cached = await this.forageStore.getItem(cacheKey)
      // default 30분
      let cacheTime = 30 * 60 * 1000
      if (!(time === undefined)) {
        cacheTime = time
      }
      if (cached && (Date.now() - cached.timestamp) < cacheTime) { // build쪽에 받아서 기준으로 캐시 유효성 체크
        return cached.data
      } else {
        await this.forageStore.removeItem(cacheKey)
        return null
      }
    } catch (error) {
      console.error('Error while getting cache:', error)
      return null // 오류가 발생하면 null 반환
    }
  }

  // 캐시 저장
  async _setCache (config, data) {
    const cacheKey = this._getCacheKey(config)

    await this.forageStore.setItem(cacheKey, {
      data,
      timestamp: Date.now() // 저장할 때 현재 시간 기록
    })
  }

  params (params, encodeFlag) {
    const newParams = {}
    for (const k in params) {
      if (typeof (params[k]) === 'string') {
        if (!encodeFlag) {
          newParams[k] = params[k]
        } else {
          newParams[k] = encodeURIComponent(params[k])
        }
      } else {
        newParams[k] = params[k]
      }
    }
    params = newParams
    this.config = this.resetConfig()
    this.config.params = params
    return this
  }

  data (data) {
    this.config = this.resetConfig()
    // build함수를 여기서 호출
    this.config.data = data
    return this
  }

  baseURL (baseURL) {
    this.config.baseURL = baseURL
    return this
  }

  contentType (contentType) {
    this.config.headers.contentType = contentType
    return this
  }

  responseType (responseType) {
    this.config.responseType = responseType
    return this
  }

  withCredentials (withCredentials) {
    this.config.withCredentials = withCredentials
    return this
  }

  setRequestInterceptors (beforeInterceptor, errorInterceptor) {
    this.requestInterceptors.push({
      before: (config) => {
        if (typeof window !== 'undefined' && window.$nuxt) {
          window.$nuxt.$startLoading()
        }
        beforeInterceptor()
        return config
      },
      error: (error) => {
        if (typeof window !== 'undefined' && window.$nuxt) {
          window.$nuxt.$finishLoading()
        }
        errorInterceptor()
        return Promise.reject(error)
      }
    })
    return this
  }

  setResponseInterceptors (beforeInterceptor, errorInterceptor) {
    this.responseInterceptors.push({
      before: (config) => {
        if (typeof window !== 'undefined' && window.$nuxt) {
          window.$nuxt.$startLoading()
        }
        beforeInterceptor()
        return config
      },
      error: (error) => {
        if (typeof window !== 'undefined' && window.$nuxt) {
          window.$nuxt.$finishLoading()
        }
        errorInterceptor()
        return Promise.reject(error)
      }
    })
    return this
  }

  build (time) {
    const instance = axios.create(this.config)
    const buildConfig = Object.assign({}, this.config)

    instance.interceptors.request.use(async (config) => {
      // config.data = this.config.data
      config.data = buildConfig.data
      const cachedResponse = await this._getCache(time, config)
      if (cachedResponse) {
        // 캐시된 데이터가 있으면, 요청을 취소하고 캐시된 데이터를 반환
        const error = new Error('Request canceled due to cache hit') // 오류 객체 생성
        error.isCached = true
        error.cachedResponse = cachedResponse // 캐시된 응답을 오류 객체에 저장
        console.log('캐싱응답 처리')
        return Promise.reject(error) // 오류 객체를 반환
      }

      console.log('Request config:', this.config)
      window.$nuxt.$store.commit('loading/incrementRequest')
      return config
    }, (error) => {
      return Promise.reject(error)
    })

    instance.interceptors.response.use(async (response) => {
      await this._setCache(response.config, response.data) // 응답 데이터를 캐시
      window.$nuxt.$store.commit('loading/decrementRequest')
      return response
    }, (error) => {
      if (axios.isCancel(error)) {
        // Request was cancelled
      } else if (error.isCached) {
        return Promise.resolve({
          data: error.cachedResponse,
          status: 200,
          statusText: 'OK',
          success: true,
          headers: {},
          config: error.config
        })
      }
      window.$nuxt.$store.commit('loading/decrementRequest')
      return Promise.reject(error)
    })

    this.requestInterceptors.forEach((interceptor) => {
      instance.interceptors.request.use(interceptor.before, interceptor.error)
    })

    this.responseInterceptors.forEach((interceptor) => {
      instance.interceptors.response.use(interceptor.before, interceptor.error)
    })

    return instance
  }

  resetConfig () {
    this.requestInterceptors = []
    this.responseInterceptors = []
    return {
      baseURL: getApiBaseUrl(),
      headers: {},
      params: {},
      withCredentials: true
    }
  }

  // [DRNQ-3083] 캐시 강제 삭제를 위한 메서드 추가
  async clearCachedSearchResults (key) {
    try {
      if (!this.forageStore) {
        return
      }
      const keyList = await this.forageStore.keys()
      const matchingKeys = keyList.filter(k => k.includes(key))
      for (const k of matchingKeys) {
        await this.forageStore.removeItem(k)
      }
    } catch (error) {
      console.error('Error clearing post cache:', error)
    }
  }
}

export default ({ $axios }, inject) => {
  inject('AxiosBuilderPostCache', new AxiosBuilderPostCache())
}
